import React, { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------
const FLOOR_VERT = `
    precision mediump float;
    attribute float alpha;
    varying float vAlpha;
    uniform float uSize;
    uniform float uHeight;
    void main() {
        vAlpha = alpha;
        vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * projectionMatrix[1][1] * uHeight * 0.5 / -mvPos.z;
        gl_Position = projectionMatrix * mvPos;
    }
`;

const FLOOR_FRAG = `
    precision mediump float;
    uniform sampler2D uMap;
    varying float vAlpha;
    void main() {
        vec4 tex = texture2D(uMap, gl_PointCoord);
        if (tex.a * vAlpha < 0.05) discard;
        gl_FragColor = vec4(tex.rgb, tex.a * vAlpha);
    }
`;

// ---------------------------------------------------------------------------
// Texture — cross icon (#9ACEF8)
// ---------------------------------------------------------------------------
function buildFloorTexture(): THREE.CanvasTexture {
    const sz = 64;
    const canvas = document.createElement("canvas");
    canvas.width = sz;
    canvas.height = sz;
    const ctx = canvas.getContext("2d")!;
    const arm = Math.round(sz * 0.18 / 6);
    const c = Math.floor((sz - arm) / 2);
    ctx.fillStyle = "#9ACEF8";
    ctx.fillRect(0, c, sz, arm);
    ctx.fillRect(c, 0, arm, sz);
    return new THREE.CanvasTexture(canvas);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export interface FloorDotGridProps {
    /** World-space Y position of the grid plane */
    floorY?: number;
    /** Half-extent in X and Z (dots span ±extent) */
    extent?: number;
    /** Spacing between dots */
    step?: number;
    /** Point size multiplier passed to shader */
    dotSize?: number;
}

const FADE_POWER = 1.8;

const FloorDotGrid: React.FC<FloorDotGridProps> = ({
    floorY  = -3.2,
    extent  = 20,
    step    = 1.2,
    dotSize = 0.5,
}) => {
    const { size } = useThree();
    const matRef = useRef<THREE.ShaderMaterial>(null);

    const texture  = useMemo(buildFloorTexture, []);

    const geometry = useMemo(() => {
        const positions: number[] = [];
        const alphas:    number[] = [];
        for (let x = -extent; x <= extent + 0.001; x += step) {
            for (let z = -extent; z <= extent + 0.001; z += step) {
                positions.push(x, 0, z);
                const d = Math.sqrt(x * x + z * z);
                alphas.push(Math.max(0, 1 - Math.pow(d / extent, FADE_POWER)));
            }
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        g.setAttribute("alpha",    new THREE.Float32BufferAttribute(alphas, 1));
        return g;
    }, [extent, step]);

    const uniforms = useMemo(() => ({
        uMap:    { value: texture },
        uSize:   { value: dotSize },
        uHeight: { value: size.height },
    }), [texture, dotSize, size.height]);

    useFrame(() => {
        if (matRef.current) matRef.current.uniforms.uHeight.value = size.height;
    });

    return (
        <points geometry={geometry} position={[0, floorY, 0]}>
            <shaderMaterial
                ref={matRef}
                uniforms={uniforms}
                vertexShader={FLOOR_VERT}
                fragmentShader={FLOOR_FRAG}
                transparent
                depthWrite={false}
            />
        </points>
    );
};

export default FloorDotGrid;
